import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../utils/api";
import { formatDateFR } from "../utils/format";

const EMPTY_ACTION = { priority: "7j", anomaly: "", action: "", responsible: "", deadline: "", status: "En attente" };

export default function ActionPlanPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [actions, setActions] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_ACTION);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getVisit(id).then(v => { setVisit(v); setActions(v.actions || []); });
  }, [id]);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function saveAction() {
    setSaving(true);
    try {
      if (editId) {
        const updated = await api.updateAction(editId, form);
        setActions(a => a.map(x => x.id === editId ? updated : x));
      } else {
        const created = await api.createAction(id, form);
        setActions(a => [...a, created]);
      }
      setAdding(false); setEditId(null); setForm(EMPTY_ACTION);
    } finally { setSaving(false); }
  }

  async function deleteAction(actionId) {
    if (!window.confirm("Supprimer cette action ?")) return;
    await api.deleteAction(actionId);
    setActions(a => a.filter(x => x.id !== actionId));
  }

  function startEdit(action) {
    setEditId(action.id);
    setForm({ priority: action.priority, anomaly: action.anomaly, action: action.action,
      responsible: action.responsible, deadline: action.deadline, status: action.status });
    setAdding(true);
  }

  const priorityColor = { "Imm.": "red", "7j": "orange", "15j": "blue", "30j": "green" };

  return (
    <div>
      <div className="page-header-row page-header">
        <div>
          <h1>Plan d'actions correctives</h1>
          <p>{visit?.store_name} · {visit?.visit_date ? formatDateFR(visit.visit_date) : "—"}</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setAdding(true); setEditId(null); setForm(EMPTY_ACTION); }}>
          ➕ Ajouter une action
        </button>
      </div>

      {/* Add/Edit form */}
      {adding && (
        <div className="card" style={{ marginBottom: 20, borderLeft: "4px solid var(--navy)" }}>
          <div style={{ fontWeight: 700, marginBottom: 16, color: "var(--navy)" }}>
            {editId ? "Modifier l'action" : "Nouvelle action corrective"}
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Priorité</label>
              <select value={form.priority} onChange={e => setF("priority", e.target.value)}>
                <option value="Imm.">Immédiate</option>
                <option value="7j">7 jours</option>
                <option value="15j">15 jours</option>
                <option value="30j">30 jours</option>
              </select>
            </div>
            <div className="form-group">
              <label>Responsable</label>
              <input value={form.responsible} onChange={e => setF("responsible", e.target.value)} placeholder="Nom du responsable" />
            </div>
            <div className="form-group full">
              <label>Anomalie constatée</label>
              <textarea value={form.anomaly} onChange={e => setF("anomaly", e.target.value)} rows={2} placeholder="Décrivez l'anomalie observée..." />
            </div>
            <div className="form-group full">
              <label>Action à mettre en place</label>
              <textarea value={form.action} onChange={e => setF("action", e.target.value)} rows={2} placeholder="Action corrective à réaliser..." />
            </div>
            <div className="form-group">
              <label>Délai prévu</label>
              <input type="date" value={form.deadline} onChange={e => setF("deadline", e.target.value)} />
            </div>
            <div className="form-group">
              <label>Statut</label>
              <select value={form.status} onChange={e => setF("status", e.target.value)}>
                <option>En attente</option>
                <option>En cours</option>
                <option>Réalisée</option>
                <option>Non réalisée</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button className="btn btn-outline" onClick={() => { setAdding(false); setEditId(null); }}>Annuler</button>
            <button className="btn btn-primary" onClick={saveAction} disabled={saving}>
              {saving ? "..." : editId ? "✏️ Modifier" : "➕ Ajouter"}
            </button>
          </div>
        </div>
      )}

      {/* Actions table */}
      {actions.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-icon">✓</div>
          <h3>Aucune action pour l'instant</h3>
          <p>Ajoutez des actions correctives si des anomalies ont été identifiées.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="action-table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th>Priorité</th><th>Anomalie</th><th>Action</th><th>Responsable</th><th>Délai</th><th>Statut</th><th></th>
              </tr></thead>
              <tbody>
                {actions.map(a => (
                  <tr key={a.id}>
                    <td>
                      <span className={`badge badge-${priorityColor[a.priority] || "routine"}`}>{a.priority}</span>
                    </td>
                    <td style={{ maxWidth: 200 }}>{a.anomaly}</td>
                    <td style={{ maxWidth: 200 }}>{a.action}</td>
                    <td>{a.responsible}</td>
                    <td>{formatDateFR(a.deadline)}</td>
                    <td>
                      <span className={`badge ${a.status === "Réalisée" ? "badge-done" : a.status === "En cours" ? "badge-progress" : "badge-routine"}`}>
                        {a.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-outline btn-sm btn-icon" onClick={() => startEdit(a)} title="✏️ Modifier">✏️</button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => deleteAction(a.id)} title="Supprimer">✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="step-actions">
        <button className="btn btn-outline" onClick={() => navigate(`/visits/${id}/checklist`)}>← Retour checklist</button>
        <button className="btn btn-primary" onClick={() => navigate(`/visits/${id}/summary`)}>Récapitulatif →</button>
      </div>
    </div>
  );
}
